import React from "react";
import { styled } from "@glitz/react";
import { GrCheckmark } from "react-icons/gr";
import {
  black,
  darkGrey,
  fontSize,
  fontWeight,
  grey,
  spacing,
  white,
} from "../../theme";

export type WizardStep = {
  /** 1-baserat stegnummer. */
  number: number;
  label: string;
};

type Props = {
  steps: WizardStep[];
  /** 1-baserat — vilket steg är aktivt just nu. */
  currentStep: number;
  /** Valfri callback när användaren klickar på ett färdigt steg för att hoppa bakåt. */
  onStepChange?: (step: number) => void;
  /** Tolkas som "allt klart" — sista steget blir bockat och ingen klickbar bakåtnavigation. */
  completed?: boolean;
};

const WizardSteps: React.FC<Props> = ({
  steps,
  currentStep,
  onStepChange,
  completed = false,
}) => {
  return (
    <Wrapper className="wizard-steps">
      {steps.map((step, index) => {
        const isCompleted = step.number < currentStep || completed;
        const isActive = step.number === currentStep && !completed;
        const isReachable =
          !completed && isCompleted && onStepChange != null;
        return (
          <StepWrapper key={step.number}>
            <Circle
              className="wizard-step-circle"
              css={[
                (isActive || isCompleted) && activeCircle,
                isReachable && clickableCircle,
              ]}
              role={isReachable ? "button" : undefined}
              tabIndex={isReachable ? 0 : undefined}
              onClick={() => {
                if (isReachable) onStepChange!(step.number);
              }}
              onKeyDown={(e) => {
                if (!isReachable) return;
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onStepChange!(step.number);
                }
              }}
              aria-current={isActive ? "step" : undefined}
              aria-label={`Steg ${step.number}: ${step.label}${
                isCompleted ? " (klar)" : isActive ? " (pågår)" : ""
              }`}
            >
              {isCompleted ? <GrCheckmark aria-hidden /> : step.number}
            </Circle>
            <Label css={(isActive || isCompleted) && activeLabel}>
              {step.label}
            </Label>
            {index !== steps.length - 1 && (
              <Line
                css={isCompleted && activeLine}
                className="wizard-step-line"
                aria-hidden
              />
            )}
          </StepWrapper>
        );
      })}
    </Wrapper>
  );
};

export default WizardSteps;

const Wrapper = styled.div({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: { x: spacing.m, y: 0 },
  position: "relative",
});

const StepWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  flex: { grow: "1", shrink: "1", basis: "1" },
  position: "relative",
});

const Circle = styled.div({
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  backgroundColor: grey,
  color: darkGrey,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: fontWeight.bold,
  fontSize: fontSize.s,
  zIndex: 1,
  transitionProperty: "background-color, color",
  transitionDuration: "0.2s",
});

const activeCircle = styled({
  backgroundColor: black,
  color: white,
});

const clickableCircle = styled({
  cursor: "pointer",
  ":hover": { opacity: 0.85 },
});

const Label = styled.span({
  marginTop: spacing.xs,
  fontSize: fontSize.xs,
  color: darkGrey,
  textAlign: "center",
  fontWeight: fontWeight.bold,
});

const activeLabel = styled({
  color: black,
});

const Line = styled.div({
  position: "absolute",
  top: "16px",
  left: "50%",
  width: "100%",
  height: "2px",
  backgroundColor: grey,
  zIndex: 0,
});

const activeLine = styled({
  backgroundColor: black,
});
